import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionIcon, Container, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { ArrowRight, BookOpen, CreditCard, ShieldCheck, Users } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

export function AdminDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user?.role === 'god' && user.onboarding_completed === false) {
      navigate('/onboarding', { replace: true })
    }
  }, [user, navigate])

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2} c="var(--sat-text)">God Dashboard</Title>
          <Text c="var(--sat-text-muted)" mt="sm">
            Manage platform-level content, pricing, and internal operations.
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Paper
            withBorder
            radius="xl"
            p="lg"
            style={{ background: 'var(--sat-surface)', cursor: 'pointer' }}
            onClick={() => navigate('/admin/sat-plans')}
          >
            <Group justify="space-between" align="flex-start" mb="md">
              <ThemeIcon size={44} radius="xl" color="grape" variant="light">
                <CreditCard size={22} />
              </ThemeIcon>
              <ActionIcon variant="subtle" color="gray">
                <ArrowRight size={18} />
              </ActionIcon>
            </Group>

            <Text fw={700} size="lg" c="var(--sat-text)">SAT Plan Management</Text>
            <Text size="sm" c="var(--sat-text-muted)" mt={6}>
              Create, edit, activate, and remove subscription plans in `tbl_sat_plan`.
            </Text>
          </Paper>

          <Paper
            withBorder
            radius="xl"
            p="lg"
            style={{ background: 'var(--sat-surface)', cursor: 'pointer' }}
            onClick={() => navigate('/admin/questions')}
          >
            <Group justify="space-between" align="flex-start" mb="md">
              <ThemeIcon size={44} radius="xl" color="blue" variant="light">
                <BookOpen size={22} />
              </ThemeIcon>
              <ActionIcon variant="subtle" color="gray">
                <ArrowRight size={18} />
              </ActionIcon>
            </Group>

            <Text fw={700} size="lg" c="var(--sat-text)">Question Bank</Text>
            <Text size="sm" c="var(--sat-text-muted)" mt={6}>
              Review and maintain the SAT question library used across assignments and evaluation flows.
            </Text>
          </Paper>

          <Paper
            withBorder
            radius="xl"
            p="lg"
            style={{ background: 'var(--sat-surface)', cursor: 'pointer' }}
            onClick={() => navigate('/admin/users')}
          >
            <Group justify="space-between" align="flex-start" mb="md">
              <ThemeIcon size={44} radius="xl" color="teal" variant="light">
                <Users size={22} />
              </ThemeIcon>
              <ActionIcon variant="subtle" color="gray">
                <ArrowRight size={18} />
              </ActionIcon>
            </Group>

            <Text fw={700} size="lg" c="var(--sat-text)">User Management</Text>
            <Text size="sm" c="var(--sat-text-muted)" mt={6}>
              Browse users, filter by status, and manage account access from one admin workspace.
            </Text>
          </Paper>
        </SimpleGrid>

        <Paper withBorder radius="xl" p="lg" style={{ background: 'var(--sat-surface)' }}>
          <Group gap="sm" mb="xs">
            <ThemeIcon size={36} radius="xl" color="teal" variant="light">
              <ShieldCheck size={18} />
            </ThemeIcon>
            <Text fw={700} c="var(--sat-text)">Admin Access</Text>
          </Group>
          <Text size="sm" c="var(--sat-text-muted)">
            This area is reserved for the `god` role and should be used for platform-wide management tasks only.
          </Text>
        </Paper>
      </Stack>
    </Container>
  )
}
